import type { AuditEntry, FileDetail, IssueStartResponse, MovementRecord } from '@jmc/shared';
import { formatDate, formatDateTime, MOVEMENT_PURPOSE_LABELS, MOVEMENT_PURPOSES } from '@jmc/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { api, ApiRequestError } from '../lib/api.js';
import { AuditTable } from '../components/AuditTrail.js';
import { useAuth } from '../lib/auth.js';
import { useI18n } from '../lib/i18n.js';
import {
  cx,
  DetailRow,
  EmptyState,
  ErrorState,
  Field,
  InlineError,
  LoadingBlock,
  Modal,
  Mono,
  PageHeader,
  Panel,
  Select,
  Spinner,
  StatusBadge,
  TextInput,
  Toast,
} from '../components/ui.js';

interface Recipient {
  id: number;
  fullName: string;
  designation: string | null;
  mobileMasked: string;
}

export function FileDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t, pick } = useI18n();
  const { can } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const routeLocation = useLocation();
  const [issueOpen, setIssueOpen] = useState(false);
  const [returnOpen, setReturnOpen] = useState(false);
  // The entry form redirects here after saving and passes its confirmation in
  // route state, so the message survives the navigation.
  const [toast, setToast] = useState<string | null>(
    (routeLocation.state as { toast?: string } | null)?.toast ?? null,
  );

  const query = useQuery({
    queryKey: ['file', id],
    queryFn: () => api<{ file: FileDetail; movements: MovementRecord[] }>(`/files/${id}/history`),
    enabled: Boolean(id),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['file', id] });
    void queryClient.invalidateQueries({ queryKey: ['files'] });
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    void queryClient.invalidateQueries({ queryKey: ['movements'] });
  };

  if (query.isLoading) return <LoadingBlock />;
  if (query.isError) {
    return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  }
  if (!query.data) return null;

  const { file, movements } = query.data;

  return (
    <>
      <button type="button" onClick={() => navigate(-1)} className="btn-ghost no-print mb-3 -ml-2">
        <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true">
          <path d="m11.5 5-5 5 5 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        {t('action.back')}
      </button>

      <PageHeader
        title={file.eFileNumber}
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <StatusBadge status={file.status} overdue={file.isOverdue} />
            <span className="text-ink-500">{t(`kind.${file.kind}`)}</span>
          </span>
        }
        actions={
          <>
            <Link to={`/files/${file.id}/print`} className="btn-secondary no-print">
              {t('action.printForm')}
            </Link>
            {can('file:update') && file.status !== 'WEEDED' && (
              <Link to={`/files/${file.id}/edit`} className="btn-secondary no-print">
                {t('action.edit')}
              </Link>
            )}
            {can('movement:issue') && file.status === 'AVAILABLE' && (
              <button type="button" className="btn-primary no-print" onClick={() => setIssueOpen(true)}>
                {t('action.issue')}
              </button>
            )}
            {can('movement:return') && file.status === 'ISSUED' && (
              <button type="button" className="btn-primary no-print" onClick={() => setReturnOpen(true)}>
                {t('action.return')}
              </button>
            )}
          </>
        }
      />

      {file.status === 'ISSUED' && (
        <div
          className={cx(
            'mb-4 rounded-md border px-4 py-3',
            file.isOverdue
              ? 'border-amber-line bg-amber-bg'
              : 'border-navy-100 bg-navy-50',
          )}
        >
          <p className={cx('font-medium', file.isOverdue && 'text-amber-ink')}>
            {t('field.issuedTo')}: {file.issuedTo}
          </p>
          <p className="text-ink-600 text-[0.8125rem]">
            {t('field.issuedOn')} {formatDateTime(file.issuedAt)} · {t('field.expectedReturn')}{' '}
            {formatDate(file.expectedReturnDate)}
            {file.isOverdue && ` · ${t('status.overdue')}`}
          </p>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <Panel title={t('field.location')}>
            {file.location ? (
              <div className="grid grid-cols-2 gap-px bg-line sm:grid-cols-4">
                {(
                  [
                    ['field.room', file.location.roomCode],
                    ['field.rack', file.location.rackCode],
                    ['field.column', file.location.columnCode],
                    ['field.bag', file.location.bagCode],
                  ] as const
                ).map(([label, value]) => (
                  <div key={label} className="bg-white px-4 py-3">
                    <p className="text-[0.75rem] uppercase tracking-[0.04em] text-ink-500">
                      {t(label)}
                    </p>
                    <p className="mt-0.5 text-lg font-semibold tabular">{value}</p>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState title={t('common.none')} />
            )}
          </Panel>

          <Panel title={t('field.subject')}>
            <dl>
              <DetailRow label={t('field.fileNumber')}>
                {file.fileNumber ? <Mono>{file.fileNumber}</Mono> : '—'}
              </DetailRow>
              <DetailRow label={t('field.subject')}>
                {pick(file.subjectEn, file.subjectHi) || '—'}
              </DetailRow>
              {file.kind === 'PROPERTY' && (
                <>
                  <DetailRow label={t('field.allottee')}>
                    {pick(file.firstAllotteeEn, file.firstAllotteeHi) || '—'}
                  </DetailRow>
                  <DetailRow label={t('field.scheme')}>
                    {pick(file.schemeNameEn, file.schemeNameHi) || '—'}
                  </DetailRow>
                  <DetailRow label={t('field.society')}>{file.societyName ?? '—'}</DetailRow>
                  <DetailRow label={t('field.plotNo')}>{file.plotNo ?? '—'}</DetailRow>
                  <DetailRow label={t('field.leaseAcNo')}>
                    {file.leaseAcNo ? <Mono>{file.leaseAcNo}</Mono> : '—'}
                  </DetailRow>
                </>
              )}
              <DetailRow label={t('field.ward')}>{file.wardNo ?? '—'}</DetailRow>
              <DetailRow label={t('field.zone')}>{file.zoneName ?? '—'}</DetailRow>
              <DetailRow label={t('field.department')}>{file.departmentName ?? '—'}</DetailRow>
              <DetailRow label={t('field.year')}>{file.financialYear ?? '—'}</DetailRow>
              {file.leaseDescription && (
                <DetailRow label="Address">{file.leaseDescription}</DetailRow>
              )}
              {file.remark && (
                <DetailRow label={t('field.remark')}>
                  <span className="whitespace-pre-line">{file.remark}</span>
                </DetailRow>
              )}
            </dl>
          </Panel>
        </div>

        <Panel title={t('history.title')} bodyClassName="px-4 py-3">
          {movements.length === 0 ? (
            <p className="py-6 text-center text-ink-500">{t('history.empty')}</p>
          ) : (
            <ol className="space-y-4">
              {movements.map((movement) => (
                <li key={movement.id} className="relative pl-5">
                  <span
                    className={cx(
                      'absolute left-0 top-1.5 h-2 w-2 rounded-full',
                      movement.returnedAt ? 'bg-green-ink' : 'bg-navy-600',
                    )}
                    aria-hidden="true"
                  />
                  <p className="font-medium">{movement.issuedToName}</p>
                  <p className="text-[0.8125rem] text-ink-500">
                    {t(`field.purpose`)}:{' '}
                    {pick(
                      MOVEMENT_PURPOSE_LABELS[movement.purpose].en,
                      MOVEMENT_PURPOSE_LABELS[movement.purpose].hi,
                    )}
                  </p>
                  <p className="mt-0.5 text-[0.8125rem] text-ink-500">
                    {formatDateTime(movement.issuedAt)} → {' '}
                    {movement.returnedAt ? (
                      formatDateTime(movement.returnedAt)
                    ) : (
                      <span
                        className={cx(
                          movement.isOverdue
                            ? 'font-medium text-amber-ink'
                            : 'text-navy-600',
                        )}
                      >
                        {movement.isOverdue ? t('status.overdue') : t('status.ISSUED')}
                      </span>
                    )}
                  </p>
                  <p className="mt-0.5 text-[0.75rem] text-ink-400">
                    {t('field.issuedBy')} {movement.issuedByName}
                    {movement.otpVerifiedAt && ' · OTP ✓'}
                  </p>
                </li>
              ))}
            </ol>
          )}
        </Panel>

      </div>

      {/* Full width, below the two columns: the change list is a table with a
          before and an after value per row, and squeezed into a side column it
          scrolls sideways instead of being read.

          The movement list says the file went out and came back; this says the
          plot number was corrected, and what it used to be. */}
      {can('audit:read') && (
        <div className="mt-4">
          <FileAuditPanel fileId={file.id} />
        </div>
      )}

      {issueOpen && (
        <IssueDialog
          file={file}
          onClose={() => setIssueOpen(false)}
          onDone={() => {
            setIssueOpen(false);
            setToast(t('issue.success'));
            refresh();
          }}
        />
      )}

      {returnOpen && (
        <ReturnDialog
          file={file}
          onClose={() => setReturnOpen(false)}
          onDone={() => {
            setReturnOpen(false);
            setToast(t('return.success'));
            refresh();
          }}
        />
      )}

      {toast && <Toast message={toast} onDismiss={() => setToast(null)} />}
    </>
  );
}

// ---------------------------------------------------------------------------
// Issue, in two steps: send the code, then verify it.
// ---------------------------------------------------------------------------

function IssueDialog({
  file,
  onClose,
  onDone,
}: {
  file: FileDetail;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t, pick } = useI18n();

  const [recipientId, setRecipientId] = useState('');
  const [purpose, setPurpose] = useState<(typeof MOVEMENT_PURPOSES)[number]>('VERIFICATION');
  const [note, setNote] = useState('');
  const [expectedReturnDate, setExpectedReturnDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() + 7);
    return date.toISOString().slice(0, 10);
  });

  const [challenge, setChallenge] = useState<IssueStartResponse | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});

  const recipients = useQuery({
    queryKey: ['recipients'],
    queryFn: () => api<{ recipients: Recipient[] }>('/users/recipients'),
  });

  const start = useMutation({
    mutationFn: () =>
      api<IssueStartResponse>('/movements/issue/start', {
        method: 'POST',
        body: {
          fileId: file.id,
          issuedToUserId: Number(recipientId),
          purpose,
          purposeNote: note || null,
          expectedReturnDate,
        },
      }),
    onSuccess: (result) => {
      setChallenge(result);
      setError(null);
      setFields({});
    },
    onError: (caught) => {
      if (caught instanceof ApiRequestError) {
        setError(caught.message);
        setFields(caught.fields);
      } else setError(t('common.error'));
    },
  });

  const confirm = useMutation({
    mutationFn: () =>
      api<MovementRecord>('/movements/issue/confirm', {
        method: 'POST',
        body: { challengeId: challenge?.challengeId, code },
      }),
    onSuccess: onDone,
    onError: (caught) => {
      if (caught instanceof ApiRequestError) {
        setError(caught.message);
        setFields(caught.fields);
      } else setError(t('common.error'));
    },
  });

  const today = new Date().toISOString().slice(0, 10);

  return (
    <Modal
      open
      title={t('issue.title')}
      onClose={onClose}
      footer={
        challenge ? (
          <>
            <button
              type="button"
              className="btn-ghost"
              onClick={() => {
                setChallenge(null);
                setCode('');
                setError(null);
              }}
            >
              {t('issue.resend')}
            </button>
            <button type="button" className="btn-secondary" onClick={onClose}>
              {t('action.cancel')}
            </button>
            <button
              type="submit"
              form="issue-verify"
              className="btn-primary"
              disabled={confirm.isPending || code.length !== 6}
            >
              {confirm.isPending && <Spinner />}
              {confirm.isPending ? t('issue.verifying') : t('issue.verify')}
            </button>
          </>
        ) : (
          <>
            <button type="button" className="btn-secondary" onClick={onClose}>
              {t('action.cancel')}
            </button>
            <button
              type="submit"
              form="issue-start"
              className="btn-primary"
              disabled={start.isPending || !recipientId}
            >
              {start.isPending && <Spinner />}
              {start.isPending ? t('issue.sending') : t('issue.sendOtp')}
            </button>
          </>
        )
      }
    >
      <p className="mb-4 rounded-[5px] bg-paper px-3 py-2 text-[0.8125rem]">
        <Mono className="font-medium">{file.eFileNumber}</Mono>
        <span className="mx-2 text-ink-400">·</span>
        {pick(file.firstAllotteeEn, file.firstAllotteeHi) ||
          pick(file.subjectEn, file.subjectHi) ||
          t(`kind.${file.kind}`)}
      </p>

      {error && <div className="mb-4"><InlineError message={error} /></div>}

      {!challenge ? (
        <form
          id="issue-start"
          className="space-y-4"
          onSubmit={(event: FormEvent) => {
            event.preventDefault();
            start.mutate();
          }}
        >
          <Field label={t('issue.recipient')} error={fields['issuedToUserId']} required>
            <Select
              value={recipientId}
              onChange={(event) => setRecipientId(event.target.value)}
              required
            >
              <option value="">{t('issue.selectRecipient')}</option>
              {recipients.data?.recipients.map((person) => (
                <option key={person.id} value={person.id}>
                  {person.fullName}
                  {person.designation ? ` — ${person.designation}` : ''} ({person.mobileMasked})
                </option>
              ))}
            </Select>
          </Field>

          <Field label={t('field.purpose')} required>
            <Select
              value={purpose}
              onChange={(event) =>
                setPurpose(event.target.value as (typeof MOVEMENT_PURPOSES)[number])
              }
            >
              {MOVEMENT_PURPOSES.map((option) => (
                <option key={option} value={option}>
                  {pick(MOVEMENT_PURPOSE_LABELS[option].en, MOVEMENT_PURPOSE_LABELS[option].hi)}
                </option>
              ))}
            </Select>
          </Field>

          <Field
            label={t('field.expectedReturn')}
            error={fields['expectedReturnDate']}
            required
          >
            {/* Native date input: no picker library, and it localises itself. */}
            <TextInput
              type="date"
              min={today}
              value={expectedReturnDate}
              onChange={(event) => setExpectedReturnDate(event.target.value)}
              required
            />
          </Field>

          <Field label={t('issue.purposeNote')}>
            <TextInput value={note} onChange={(event) => setNote(event.target.value)} />
          </Field>
        </form>
      ) : (
        <form
          id="issue-verify"
          className="space-y-4"
          onSubmit={(event: FormEvent) => {
            event.preventDefault();
            confirm.mutate();
          }}
        >
          <p>
            {t('issue.otpSent')}{' '}
            <span className="font-medium tabular">{challenge.mobileMasked}</span>
          </p>

          <Field label={t('issue.otpLabel')} error={fields['code']} required>
            <TextInput
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="\d{6}"
              maxLength={6}
              autoFocus
              value={code}
              onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
              className="text-center text-xl font-semibold tracking-[0.5em] tabular"
              placeholder="······"
              required
            />
          </Field>

          <OtpCountdown expiresAt={challenge.expiresAt} />

          {challenge.devCode && (
            // Only ever present outside production -- the server refuses to
            // start in production with the console OTP sender.
            <p className="rounded-[5px] border border-dashed border-amber-line bg-amber-bg px-3 py-2 text-[0.8125rem] text-amber-ink">
              {t('issue.devCode')}: <Mono className="font-semibold">{challenge.devCode}</Mono>
            </p>
          )}
        </form>
      )}
    </Modal>
  );
}

function OtpCountdown({ expiresAt }: { expiresAt: string }) {
  const { t } = useI18n();
  const [remaining, setRemaining] = useState(() =>
    Math.max(0, Math.floor((new Date(expiresAt).getTime() - Date.now()) / 1000)),
  );

  useEffect(() => {
    const timer = setInterval(() => {
      setRemaining(Math.max(0, Math.floor((new Date(expiresAt).getTime() - Date.now()) / 1000)));
    }, 1000);
    return () => clearInterval(timer);
  }, [expiresAt]);

  const minutes = Math.floor(remaining / 60);
  const seconds = remaining % 60;

  return (
    <p className={cx('text-[0.8125rem]', remaining < 30 ? 'text-red-ink' : 'text-ink-500')}>
      {t('issue.expiresIn')}{' '}
      <span className="tabular font-medium">
        {minutes}:{String(seconds).padStart(2, '0')}
      </span>
    </p>
  );
}

// ---------------------------------------------------------------------------
// Return
// ---------------------------------------------------------------------------

function ReturnDialog({
  file,
  onClose,
  onDone,
}: {
  file: FileDetail;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useI18n();
  const [condition, setCondition] = useState<'GOOD' | 'DAMAGED' | 'INCOMPLETE'>('GOOD');
  const [note, setNote] = useState('');
  const [relocate, setRelocate] = useState(false);
  const [location, setLocation] = useState({
    roomCode: file.location?.roomCode ?? '',
    rackCode: file.location?.rackCode ?? '',
    columnCode: file.location?.columnCode ?? '',
    bagCode: file.location?.bagCode ?? '',
  });
  const [error, setError] = useState<string | null>(null);

  const submit = useMutation({
    mutationFn: () =>
      api<MovementRecord>('/movements/return', {
        method: 'POST',
        body: {
          fileId: file.id,
          condition,
          note: note || null,
          ...(relocate ? { location } : {}),
        },
      }),
    onSuccess: onDone,
    onError: (caught) =>
      setError(caught instanceof ApiRequestError ? caught.message : t('common.error')),
  });

  return (
    <Modal
      open
      title={t('return.title')}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            {t('action.cancel')}
          </button>
          <button type="submit" form="return-form" className="btn-primary" disabled={submit.isPending}>
            {submit.isPending && <Spinner />}
            {t('action.return')}
          </button>
        </>
      }
    >
      <p className="mb-4 rounded-[5px] bg-paper px-3 py-2 text-[0.8125rem]">
        <Mono className="font-medium">{file.eFileNumber}</Mono>
        <span className="mx-2 text-ink-400">·</span>
        {t('field.issuedTo')} {file.issuedTo}
      </p>

      {error && <div className="mb-4"><InlineError message={error} /></div>}

      <form
        id="return-form"
        className="space-y-4"
        onSubmit={(event: FormEvent) => {
          event.preventDefault();
          submit.mutate();
        }}
      >
        <Field label={t('return.condition')} required>
          <div className="flex flex-wrap gap-2">
            {(['GOOD', 'DAMAGED', 'INCOMPLETE'] as const).map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => setCondition(option)}
                aria-pressed={condition === option}
                className={cx(
                  'rounded-[5px] border px-3 py-1.5 transition-colors',
                  condition === option
                    ? 'border-navy-600 bg-navy-50 font-medium text-navy-700'
                    : 'border-line-strong text-ink-700 hover:border-navy-300',
                )}
              >
                {t(`return.condition.${option}`)}
              </button>
            ))}
          </div>
        </Field>

        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={relocate}
            onChange={(event) => setRelocate(event.target.checked)}
            className="h-4 w-4 rounded border-line-strong"
          />
          <span>{t('return.relocate')}</span>
        </label>

        {relocate && (
          <div className="grid grid-cols-2 gap-3 rounded-[5px] border border-line p-3 sm:grid-cols-4">
            {(
              [
                ['field.room', 'roomCode'],
                ['field.rack', 'rackCode'],
                ['field.column', 'columnCode'],
                ['field.bag', 'bagCode'],
              ] as const
            ).map(([label, key]) => (
              <Field key={key} label={t(label)} required>
                <TextInput
                  value={location[key]}
                  onChange={(event) =>
                    setLocation((current) => ({ ...current, [key]: event.target.value }))
                  }
                  required
                />
              </Field>
            ))}
          </div>
        )}

        <Field label={t('return.note')}>
          <TextInput value={note} onChange={(event) => setNote(event.target.value)} />
        </Field>
      </form>
    </Modal>
  );
}

function FileAuditPanel({ fileId }: { fileId: number }) {
  const { t } = useI18n();
  const query = useQuery({
    queryKey: ['file-audit', fileId],
    queryFn: () =>
      api<{ entries: AuditEntry[] }>(
        `/reports/audit?entity=file_record&entityId=${fileId}&limit=50`,
      ),
  });

  return (
    <Panel title={t('audit.forThisFile')} bodyClassName="overflow-x-auto">
      {query.isError ? (
        <div className="p-4">
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        </div>
      ) : (
        <AuditTable
          entries={query.data?.entries ?? []}
          loading={query.isLoading}
          showEntity={false}
        />
      )}
    </Panel>
  );
}
